import {
  BlotterTable,
  Box,
  type ColumnDef,
  columnTypes,
  Dialog,
  type DialogProps,
  formattedUTCDay,
  type SecurityColumnParams,
  useBlotterTable,
} from '@talos/kyoko';
import type { GridOptions } from 'ag-grid-community';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../../hooks';
import { type CurrentBasketDetailsRow, useCurrentBasketDetailsRows } from './useCurrentBasketDetailsRows';

export const CurrentBasketDetailsDialog = (props: DialogProps) => {
  const { useFundDefinitionAndTradableIndexesStreams } = useFeatureFlag();
  const dataObservable = useCurrentBasketDetailsRows();

  const columns = useMemo(
    () => (useFundDefinitionAndTradableIndexesStreams ? FUND_DEFINITION_COLUMNS : LEGACY_BASKET_COLUMNS),
    [useFundDefinitionAndTradableIndexesStreams]
  );

  const gridOptions = useMemo((): GridOptions<CurrentBasketDetailsRow> | undefined => {
    if (!useFundDefinitionAndTradableIndexesStreams) {
      return undefined;
    }

    return {
      groupDisplayType: 'singleColumn',
      groupDefaultExpanded: -1,
      showOpenedGroup: true,
      groupRemoveLowestSingleChildren: true,
      autoGroupColumnDef: columnTypes.group({
        width: 300,
        type: 'group',
        editable: false,
        hide: false,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
      }),
    };
  }, [useFundDefinitionAndTradableIndexesStreams]);

  const blotterTable = useBlotterTable<CurrentBasketDetailsRow>({
    dataObservable,
    errorObservable: undefined,
    rowID: 'rowID',
    columns,
    gridOptions,
  });

  return (
    <Dialog
      title="Current Basket Details in Talos"
      showCancel={false}
      width="600px"
      {...props}
      showClose
      showConfirm={false}
    >
      <Box h="400px" w="100%">
        {props.isOpen && <BlotterTable {...blotterTable} />}
      </Box>
    </Dialog>
  );
};

const LEGACY_BASKET_COLUMNS: ColumnDef<CurrentBasketDetailsRow>[] = [
  {
    type: 'text',
    field: 'fundSymbol',
    title: 'Fund',
    width: 80,
  },
  {
    type: 'security',
    field: 'compoundSymbol',
    title: 'Underlying Symbol',
    width: 140,
    params: { showIcon: false } satisfies SecurityColumnParams,
  },
  {
    type: 'date',
    field: 'basketRatioDate',
    title: 'Basket Ratio Date',
    width: 140,
    params: {
      customFormatter: formattedUTCDay,
    },
  },
  {
    type: 'number',
    field: 'basketRatio',
    title: 'Basket Ratio',
  },
];

const FUND_DEFINITION_COLUMNS: ColumnDef<CurrentBasketDetailsRow>[] = [
  {
    type: 'text',
    field: 'fundSymbol',
    title: 'Fund',
    width: 140,
    rowGroup: true,
    rowGroupIndex: 0,
    hide: true,
    enableRowGroup: true,
  },
  {
    type: 'security',
    field: 'compoundSymbol',
    title: 'Symbol',
    width: 160,
    rowGroup: true,
    rowGroupIndex: 1,
    hide: true,
    enableRowGroup: true,
    params: { showIcon: false } satisfies SecurityColumnParams,
  },
  {
    type: 'security',
    field: 'legSymbol',
    title: 'Leg Symbol',
    width: 160,
    rowGroup: true,
    rowGroupIndex: 2,
    hide: true,
    enableRowGroup: true,
    params: { showIcon: false } satisfies SecurityColumnParams,
  },
  {
    type: 'number',
    field: 'sizeRatio',
    title: 'Size Ratio',
    width: 200,
  },
];
